import React, { Component } from 'react';
import { Table, Badge, Row, Col, Card } from 'react-bootstrap';
import gql from 'graphql-tag';
import { withApollo } from 'react-apollo';

const GET_ACADEMIC_PERFORMANCE_REPORT = gql`
  query getStudentAcademicPerformanceReport($student_id: Int!) {
    getStudentAcademicPerformanceReport(student_id: $student_id) {
      exam_phase
      academic_year
      academic_year_id
      exam_standard
      student_id
      student_name
      gender
      roll_no
      admission_no
      subjects {
        subject
        subject_id
        color_code
        marks
        internal_max_marks
        class_id
        class_name
        exam_phase_internals
        grade
        grade_point
      }
    }
  }
`;

class StudentAcademicPerformance extends Component {
    constructor(props) {
        super(props);
        this.state = {
            performanceData: [],
            loading: false
        };
    }

    componentDidMount() {
        this.fetchPerformanceReport();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.student?.student_id !== this.props.student?.student_id) {
            this.fetchPerformanceReport();
        }
    }

    fetchPerformanceReport = async () => {
        const { student, client } = this.props;
        const studentId = student?.student_id;
        if (!studentId || !client) return;

        this.setState({ loading: true });
        try {
            const result = await client.query({
                query: GET_ACADEMIC_PERFORMANCE_REPORT,
                variables: { student_id: parseInt(studentId) },
                fetchPolicy: 'network-only'
            });
            this.setState({
                performanceData: result.data?.getStudentAcademicPerformanceReport || [],
                loading: false
            });
        } catch (error) {
            console.error("Error fetching student academic performance:", error);
            this.setState({ loading: false });
        }
    };

    render() {
        const { performanceData, loading } = this.state;

        if (loading) {
            return (
                <div className="py-4">
                    <style>
                        {`
                            @keyframes shimmerLoading {
                                0% { background-position: -1000px 0; }
                                100% { background-position: 1000px 0; }
                            }
                            .shimmer-line {
                                height: 20px;
                                margin-bottom: 15px;
                                background: #f6f7f8;
                                background-image: linear-gradient(to right, #f6f7f8 0%, #edeef1 20%, #f6f7f8 40%, #f6f7f8 100%);
                                background-repeat: no-repeat;
                                background-size: 1000px 100%;
                                animation: shimmerLoading 1.5s infinite linear forwards;
                                border-radius: 4px;
                            }
                            .shimmer-header { height: 40px; margin-bottom: 24px; border-radius: 8px; }
                        `}
                    </style>
                    <div className="shimmer-line shimmer-header" style={{ width: '100%' }}></div>
                    <div className="shimmer-line" style={{ width: '100%' }}></div>
                    <div className="shimmer-line" style={{ width: '100%' }}></div>
                </div>
            );
        }

        // Get unique subjects and exam phases
        const phases = Array.from(new Set(performanceData.map(p => p.exam_phase)));
        const subjectsMap = new Map();

        performanceData.forEach(phase => {
            phase.subjects.forEach(sub => {
                if (!subjectsMap.has(sub.subject)) {
                    subjectsMap.set(sub.subject, {
                        name: sub.subject,
                        color: sub.color_code || '#64748b',
                        marksByPhase: {} // phaseName -> { marks, grade, gradePoint }
                    });
                }
                const subObj = subjectsMap.get(sub.subject);
                subObj.marksByPhase[phase.exam_phase] = {
                    marks: sub.marks,
                    grade: sub.grade || "—",
                    gradePoint: sub.grade_point || "—"
                };
            });
        });

        const subjectsList = Array.from(subjectsMap.values());

        // Calculate stats for Summary Cards
        let totalMarksSum = 0;
        let marksCount = 0;
        const subjectTotals = {}; // subjectName -> { sum, count }

        performanceData.forEach(phase => {
            phase.subjects.forEach(sub => {
                const marksVal = parseFloat(sub.marks) || 0;
                totalMarksSum += marksVal;
                marksCount++;

                if (!subjectTotals[sub.subject]) {
                    subjectTotals[sub.subject] = { sum: 0, count: 0 };
                }
                subjectTotals[sub.subject].sum += marksVal;
                subjectTotals[sub.subject].count++;
            });
        });

        const overallAvg = marksCount > 0 ? (totalMarksSum / marksCount).toFixed(1) : "0.0";

        let topSubject = "—";
        let maxSubAvg = -1;
        Object.keys(subjectTotals).forEach(subName => {
            const avg = subjectTotals[subName].sum / subjectTotals[subName].count;
            if (avg > maxSubAvg) {
                maxSubAvg = avg;
                topSubject = subName;
            }
        });

        // Compute Phase Totals for the "TOTAL" row
        const phaseTotals = {};
        phases.forEach(phase => {
            let sum = 0;
            let count = 0;
            subjectsList.forEach(sub => {
                const phaseMark = sub.marksByPhase[phase];
                if (phaseMark && phaseMark.marks !== undefined) {
                    sum += parseFloat(phaseMark.marks) || 0;
                    count++;
                }
            });
            phaseTotals[phase] = { sum, count };
        });

        return (
            <div>
                {/* 1. Summary Cards */}
                <Row className="mb-4">
                    <Col md={4} className="mb-3 mb-md-0">
                        <Card className="border-0 shadow-sm p-3" style={{ borderRadius: '12px', borderLeft: '4px solid #10b981' }}>
                            <div className="text-muted small font-weight-bold text-uppercase">Academic Score Avg</div>
                            <h3 className="font-weight-bold text-success mt-2 mb-0">{overallAvg}%</h3>
                            <span className="text-muted small mt-1 d-block">Overall performance across subjects</span>
                        </Card>
                    </Col>
                    <Col md={4} className="mb-3 mb-md-0">
                        <Card className="border-0 shadow-sm p-3" style={{ borderRadius: '12px', borderLeft: '4px solid #6366f1' }}>
                            <div className="text-muted small font-weight-bold text-uppercase">Top Subject</div>
                            <h3 className="font-weight-bold text-primary mt-2 mb-0">{topSubject}</h3>
                            <span className="text-muted small mt-1 d-block">Highest average score this term</span>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="border-0 shadow-sm p-3" style={{ borderRadius: '12px', borderLeft: '4px solid #f59e0b' }}>
                            <div className="text-muted small font-weight-bold text-uppercase">Exams Tracked</div>
                            <h3 className="font-weight-bold text-warning mt-2 mb-0">{phases.length}</h3>
                            <span className="text-muted small mt-1 d-block">Assessment phases completed</span>
                        </Card>
                    </Col>
                </Row>

                {/* 2. Assessment Matrix Table */}
                <h5 className="mb-4 text-dark font-weight-bold">Academic Performance Overview</h5>
                {phases.length === 0 ? (
                    <div className="text-center py-5 text-muted bg-white rounded shadow-sm">No academic performance records found for this student.</div>
                ) : (
                    <div className="bg-white rounded-lg shadow-sm border overflow-hidden" style={{ borderColor: '#e2e8f0', borderRadius: '16px' }}>
                        <Table responsive borderless className="align-middle mb-0">
                            <thead>
                                {/* Phase Header Row */}
                                <tr style={{ backgroundColor: '#701a75', color: '#ffffff' }}>
                                    <th className="align-middle py-3 px-4 font-weight-bold" style={{ minWidth: '180px', color: '#fff' }}>
                                        <i className="fas fa-book-open mr-2" /> SUBJECT
                                    </th>
                                    {phases.map((phase, idx) => {
                                        const phaseObj = performanceData.find(p => p.exam_phase === phase);
                                        const examStandard = phaseObj ? phaseObj.exam_standard : "";
                                        return (
                                            <th key={idx} colSpan={2} className="text-center align-middle py-2 border-left" style={{ borderLeftColor: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: '13px', fontWeight: '700' }}>
                                                {phase} {examStandard ? `(${examStandard})` : ""}
                                            </th>
                                        );
                                    })}
                                </tr>
                                {/* Sub-headers Row */}
                                <tr style={{ backgroundColor: '#fdf4ff', borderBottom: '2px solid #e2e8f0' }}>
                                    <th className="py-2 px-4 font-weight-bold text-secondary" style={{ fontSize: '11px' }}>Name</th>
                                    {phases.map((phase, idx) => {
                                        const phaseObj = performanceData.find(p => p.exam_phase === phase);
                                        const firstSubject = phaseObj?.subjects?.[0];
                                        const internals = firstSubject?.exam_phase_internals || "Marks";
                                        const maxMarks = firstSubject?.internal_max_marks;
                                        return (
                                            <React.Fragment key={idx}>
                                                <th className="text-center py-2 border-left" style={{ borderLeftColor: '#f3e8ff', fontSize: '11px', fontWeight: '600', color: '#64748b' }}>
                                                    {internals}{maxMarks ? ` (${maxMarks})` : ""}
                                                </th>
                                                <th className="text-center py-2" style={{ fontSize: '11px', fontWeight: '600', color: '#64748b' }}>Grade/GPA</th>
                                            </React.Fragment>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {/* Subject Rows */}
                                {subjectsList.map((sub, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid #f3e8ff' }}>
                                        <td className="py-3 px-4 font-weight-bold text-dark d-flex align-items-center" style={{ gap: '8px' }}>
                                            <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: sub.color }}></span>
                                            {sub.name}
                                        </td>
                                        {phases.map((phase, pIdx) => {
                                            const phaseMark = sub.marksByPhase[phase];
                                            return (
                                                <React.Fragment key={pIdx}>
                                                    <td className="text-center py-3 font-weight-bold border-left" style={{ borderLeftColor: '#fdf4ff', color: '#1e293b' }}>
                                                        {phaseMark ? phaseMark.marks : "—"}
                                                    </td>
                                                    <td className="text-center py-3">
                                                        {phaseMark ? (
                                                            <Badge style={{
                                                                fontSize: '11px',
                                                                backgroundColor: phaseMark.grade.startsWith('A') ? '#dcfce7' : phaseMark.grade.startsWith('B') ? '#dbeafe' : '#fee2e2',
                                                                color: phaseMark.grade.startsWith('A') ? '#15803d' : phaseMark.grade.startsWith('B') ? '#1d4ed8' : '#b91c1c',
                                                                borderRadius: '4px',
                                                                padding: '4px 8px',
                                                                fontWeight: '700'
                                                            }}>
                                                                {phaseMark.grade}
                                                            </Badge>
                                                        ) : "—"}
                                                    </td>
                                                </React.Fragment>
                                            );
                                        })}
                                    </tr>
                                ))}

                                {/* TOTAL Row */}
                                <tr className="font-weight-bold" style={{ backgroundColor: '#faf5ff', borderTop: '2px solid #e9d5ff' }}>
                                    <td className="py-3 px-4 text-purple font-weight-bold">TOTAL</td>
                                    {phases.map((phase, idx) => (
                                        <React.Fragment key={idx}>
                                            <td className="text-center py-3 border-left" style={{ borderLeftColor: '#f3e8ff', color: '#701a75' }}>
                                                {phaseTotals[phase].sum}
                                            </td>
                                            <td className="text-center py-3 text-muted" style={{ fontSize: '12px' }}>
                                                —
                                            </td>
                                        </React.Fragment>
                                    ))}
                                </tr>
                            </tbody>
                        </Table>
                    </div>
                )}
            </div>
        );
    }
}

export default withApollo(StudentAcademicPerformance);
